import { isFulfilled } from "@reduxjs/toolkit";
import { Form, Formik, FormikErrors } from "formik";
import { FC, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import {
  TFN_CLIENT_ID,
  TFN_FORM_ID,
  TFN_NAME,
  TFN_TEMPLATE_ID,
} from "../../../constants/app";
import { useImpersonation } from "../../../contexts/ImpersonationContext";
import { useReopenedStages } from "../../../hooks/useReopenedStages";
import {
  useAppSelector,
  useAppDispatch as useDispatch,
  useAppSelector as useSelector,
} from "../../../store";
import { setTfnSuperformErrorMsgAction } from "../../../store/onboarding/slice";
import {
  checkDocStatus,
  getFormFieldsThunk,
  submitTFNForm,
} from "../../../store/onboarding/thunk";
import {
  FLKFormField,
  FlkFormField,
  FlkFormPayload,
  FLKInitialValues,
  TGetFormFieldsPayload,
} from "../../../store/onboarding/types";
import { isSuperAdmin } from "../../../utils/roleHelper";
import Loader from "../../Loader";
import CustomButton from "../../UI/Buttons/CustomButton";
import Download from "../../UI/Icons/Download";
import LeftArrow from "../../UI/Icons/LeftArrow";
import RightArrow from "../../UI/Icons/RightArrow";
import Warning from "../../UI/Icons/Warning";
import DynamicRadioInput from "../../UI/Inputs/DynamicRadioInput";
import Label from "../../UI/Inputs/label/Label";
import SplitInput from "../../UI/Inputs/SplitInput";
import ErrorMessage from "../../UI/Tooltips/ErrorMessage";


/**
 * TfnForm Component
 *
 * This component is responsible for rendering and managing the Tax File Number (TFN) Declaration form.
 * It integrates with Redux for state management and uses Formik for form handling and validation.
 *
 * @component
 *
 * @returns {JSX.Element} The rendered TFN Declaration form component.
 *
 * @description
 * - Fetches and displays TFN form fields dynamically based on the provided payload.
 * - Handles form submission, validation, and error handling.
 * - Supports split-input fields, radio groups, and dynamic HTML rendering.
 * - Provides functionality to download the generated TFN Declaration PDF.
 * - Allows editing of the TFN form after submission.
 *
 * @state
 * - `tfnFormFields` (`FLKFormField[]`): Stores the dynamically loaded TFN form fields.
 * - `tfnInitialValues` (`FLKInitialValues[]`): Stores the initial values for the form fields.
 * - `isDisabled` (`boolean`): Tracks whether certain buttons are disabled.
 * - `serverErrorMsg` (`string`): Stores error messages from the server.
 * - `FlkClientUrl` (`string`): Stores the URL for the external TFN form viewer.
 * - `FlkPdfUrl` (`string`): Stores the URL for the generated TFN Declaration PDF.
 *
 * @redux
 * - `formData`: Retrieved from the Redux store, contains the TFN form data.
 * - `dispatch`: Used to dispatch Redux actions such as `getFormFieldsThunk`, `submitTFNForm`, and `checkDocStatus`.
 *
 * @methods
 * - `loadTfnFormFields`: Loads the TFN form fields from the server and prepares initial values.
 * - `prepareInitialValues`: Prepares the initial values for the form fields.
 * - `finalUserInput`: Handles user input for split-input field types.
 * - `handleSetFormValues`: Updates the state with the user's input for form fields.
 * - `handleFormSubmitValue`: Submits the TFN form data to the server.
 * - `handleDocStatus`: Checks the status of the candidate's document.
 * - `EditTfnForm`: Allows the user to edit the TFN form after submission.
 * - `handleDownloadPdf`: Triggers the download of the generated TFN Declaration PDF.
 * - `errorSummary`: Displays a summary of validation errors to the user.
 *
 * @dependencies
 * - `Formik`: Used for form handling and validation.
 * - `useDispatch`, `useSelector`: Redux hooks for dispatching actions and accessing the store.
 * - `CustomButton`, `SplitInput`, `DynamicRadioInput`, `Label`, `ErrorMessage`: Custom UI components.
 *
 * @example
 * ```tsx
 * <TfnForm />
 * ```
 */

const TfnForm: FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const formData = useSelector((state) => state.onboarding.tfnForm);
  const user = useSelector((state) => state.auth.user);
  const errorMsg = useSelector((state) => state.onboarding.errorMessage)
  const {isImpersonating, impersonationData} = useImpersonation();  

  // State Declaration
  const [tfnFormFields, setTfnFormFields] = useState<FLKFormField[]>([]);
  const [clearTFNFieldValues, setClearTFNFieldValues] = useState<boolean>(false);
  const [tfnInitialValues, setTfnInitialValues] = useState<FLKInitialValues>(
    {}
  );
  const [isDisabled, setIsDisabled] = useState<boolean>(true);
  const [serverErrorMsg, setServerErrorMsg] = useState<string>("");
  const [FlkClientUrl, setFlkClientUrl] = useState<string>("");
  const [FlkPdfUrl, setFlkPdfUrl] = useState<string>("");
  const [doTFNFieldValidation, setDoTFNFieldValidation] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const isProgress = useAppSelector((state) => state.onboarding.inProgress);
  const isAdmin = isSuperAdmin(user);

  const superAdminCandidateData = useSelector(
    (state) => state.superadmin.reviewCandidateData);

  // Variable Devlaration
  let dynamicIsDisabled = true;
  const initialValues: FLKInitialValues = {};
  const getPayload: TGetFormFieldsPayload = {
    template_id: TFN_TEMPLATE_ID,
    is_super_form: "0",
    is_tfn_form: "1",
    candidate_id: JSON.stringify(isAdmin ? superAdminCandidateData?.id :  user?.id),
  };

  // Ref to track if loadTfnFormFields has already been called
  const isInitialLoad = useRef(false);

  //Loading initial form fields
  const loadTfnFormFields = (): void => {
    setIsLoading(true);
    dispatch(getFormFieldsThunk(getPayload));
  };

  useEffect(() => {
    if (!isInitialLoad.current) {
      loadTfnFormFields();
      isInitialLoad.current = true; // Mark as executed
      setIsLoading(true);
    } else {
      setIsLoading(true);
      let tfnFormData: FLKFormField[] = [];
      let s3PdfUrl: string = "";
      let signingUrl: string = "";
      s3PdfUrl = formData?.data?.s3_pdf_url || "";
      signingUrl = formData?.signingUrl || "";
      if (s3PdfUrl?.length > 0) {
        setFlkPdfUrl(s3PdfUrl);
        document.querySelector(".tfn-form")?.classList.add("d-none");
        setIsLoading(false);
      } else if(signingUrl && signingUrl?.length > 0) {
        setFlkClientUrl(isImpersonating ? "" : signingUrl);
        tfnFormData = formData?.data?.fields ? JSON.parse(formData?.data?.fields) : [];
        prepareInitialValues(tfnFormData as FLKFormField[]);
        setTfnFormFields(tfnFormData);
        setIsLoading(false);
      } else {
        tfnFormData = formData?.data?.fields ? JSON.parse(formData?.data?.fields) : [];
        prepareInitialValues(tfnFormData as FLKFormField[]);
        setTfnFormFields(tfnFormData);
        setIsLoading(false);
        document.querySelector(".flk-form")?.classList.remove("max-w-1200px");
        document.querySelector(".flk-form")?.classList.add("max-w-960px");
      }
    }
  }, [formData]);

 

  // Function to prepare initial values for the form fields
  const prepareInitialValues = (fields: FLKFormField[]) => {
    fields?.forEach((field: FLKFormField) => {
      if (!field.type?.includes("html")) {
        if (field.type === "split-input") {
          const splitLength = Array(field?.boxCount).fill(null);
          splitLength.forEach((_: number | null, index: number) => {
            initialValues[`${field.name}-${index}`] = field.value
              ? field.value?.[index]
              : "";
          });
          initialValues[field.name] = field.value;
        } else {
          initialValues[field.name] = field.value;
        }
      }
    });
    setTfnInitialValues(initialValues);
  };

  // Function to handle final user input for split input field type
  const finalUserInput = (splitLength: Array<number | null>, name: string) => {
    const values: Array<string | null> = [];
    splitLength.forEach((_: number | null, index: number) => {
      const fieldInputValue = (
        document.querySelector<HTMLInputElement>(`[name="${name}-${index}"]`)
          ?.value || ""
      ).trim();
      values.push(fieldInputValue !== "" ? fieldInputValue : null);
    });
    return values.join("").toUpperCase();
  };

  // Function to handle form field values update the state
  const handleSetFormValues = (fieldName: string, value: string) => {
    setTfnFormFields((prevFields: FLKFormField[]) =>
      prevFields.map((field: FLKFormField) => {
        if (field?.name === fieldName) {
          if (field?.type === "split-input") {
            const splitLength = Array(field?.boxCount).fill(null);
            const finalSplitInputValue = finalUserInput(
              splitLength,
              field?.name
            );
            return { ...field, value: finalSplitInputValue };
          } else {
            return { ...field, value };
          }
        }
        return field;
      })
    );
  };

  useEffect(() => {
    if(!doTFNFieldValidation) {
      tfnFormFields?.forEach((field: FLKFormField) => {
        if (field?.isTFNField === true && clearTFNFieldValues === true) {
          field.value = "";
        }
      });
    }
  }, [doTFNFieldValidation, tfnFormFields]);

  // Function to handle form submission
  const handleFormSubmitValue = async (
    values: FLKInitialValues
  ): Promise<void> => {
    dispatch(setTfnSuperformErrorMsgAction(''))
    try {
      setIsLoading(true);
      setIsDisabled(true);
      // Check if form values have changed
      const isFormChanged = Object.keys(values).some(
        (key) => values[key] !== tfnInitialValues[key]
      );

      const tfnFormData: FlkFormField[] = [
        {
          fields: tfnFormFields,
          isTFNForm: 1,
          isSuperForm: 0,
          label: TFN_NAME,
          formId: TFN_FORM_ID,
          clientId: TFN_CLIENT_ID,
          candidateId: JSON.stringify(user?.id),
        },
      ];

      const tfnFormPayloads: FlkFormPayload = {
        data: JSON.stringify(tfnFormData),
        candidate_id: JSON.stringify(user?.id), // Ensure candidate_id is always a string
        form_name: TFN_NAME,
        is_active_super_form: "0",
        is_active_tfn_form: "1",
        is_super_form_changed: "0",
        // is_tfn_form_changed: isFormChanged ? "1" : "0",
        is_tfn_form_changed: "1",
        template_id: getPayload.template_id ?? "",
      };

      const result = await dispatch(submitTFNForm(tfnFormPayloads));
      if (isFulfilled(result)) {
        const submitResponse = result.payload.data;
        if (result?.payload?.statusCode === 200) {
          setFlkClientUrl(submitResponse?.flk_client_url);
          document.querySelector(".tfn-form")?.classList.add("d-none");
          document.querySelector(".flk-form")?.classList.remove("max-w-960px");
          document.querySelector(".flk-form")?.classList.add("max-w-1200px");
          setIsDisabled(false);
          setIsLoading(false);
        }
      } else {
        setServerErrorMsg(
          "Could not connect to services at the moment. Please try again. Thanks."
        );
        setIsDisabled(false);
        setIsLoading(false);
      }
    } catch (error) {
      setIsDisabled(false);
      setIsLoading(false);
      throw new Error(
        "Could not connect to services at the moment. Please try again. Thanks."
      );
    }
  };

  // Function to handle checking the candidate document status
  const handleDocStatus = async (): Promise<void> => {
    setIsLoading(true);
    const docStatusPayload = {
      candidate_id: JSON.stringify(user?.id),
      // candidate_id: "24",
      template_id: getPayload.template_id ?? "",
    };
    const docStatusResponse = await dispatch(checkDocStatus(docStatusPayload));
    if (isFulfilled(docStatusResponse)) {
      const docStatus = docStatusResponse.payload;
      if (docStatus.data.statusCode === 200 && docStatus?.data.pdf_url.length > 0) {
        setFlkClientUrl("");
        setFlkPdfUrl(docStatus?.data.pdf_url);
        setIsLoading(false);

        if (isStage3Reopen && reopenedSections.length > 0) {
          const currentIndex = stage3Keys.indexOf("tfn_form");
          const stage3ReopenedSections = reopenedSections.filter(
            (key) => stage3Keys.includes(key)
          );
          const nextSection = stage3ReopenedSections.find(
            (key) => stage3Keys.indexOf(key) > currentIndex
          );
          if (nextSection) {
            const route = sectionRouteMap[nextSection];
            navigate(
              isAdmin
                ? `/admin/candidate-profile/${route}`
                : `/candidate/my-profile/${route}`
            );
            return;
          }
          navigate(isAdmin ? "/admin/candidate-profile/superannuation" : "/candidate/my-profile/superannuation");
        }
      } else {
        setIsLoading(false);
        toast.warn(
          "Please sign the document before proceeding to the next step."
        );
        setServerErrorMsg(
          "Please sign the document before proceeding to the next step."
        );
      }
    }
  };

  // Function to handle editing the TFN form
  const EditTfnForm = () => {
    setFlkClientUrl("");
    setServerErrorMsg("");
    setIsDisabled(false);
    document.querySelector(".flk-form")?.classList.remove("max-w-1200px");
    document.querySelector(".flk-form")?.classList.add("max-w-960px");
    document.querySelector(".tfn-form")?.classList.remove("d-none");
  };

  // Function to handle PDF download
  const handleDownloadPdf = (pdfUrl: string) => {
    const link = document.createElement("a");
    link.href = pdfUrl;
    link.target = "_blank";
    link.setAttribute("download", "TFN_Declaration.pdf"); // Ensure the download attribute is set
    document.body.appendChild(link); // Append the link to the DOM
    link.click(); // Trigger the download
    document.body.removeChild(link); // Clean up by removing the link
  };

  //Handled error summary to show the error list to the user since submit button is disable until all the field validation is passed
  interface ErrorSummaryProps {
    errors: FormikErrors<FLKInitialValues>;
    setErrors: (errors: FormikErrors<FLKInitialValues>) => void;
  }

  const errorSummary = (
    errors: FormikErrors<FLKInitialValues>,
    setErrors: (errors: FormikErrors<FLKInitialValues>) => void
  ) => {
    // Remove error keys with empty values using setErrors
    Object.entries(errors).forEach(([key, value]) => {
      if (!value) {
        // Remove the key from errors
        delete errors[key];
      }
    });
    setErrors(errors);
  };

  const stage3Keys = [
    "tfn_form",
    "superannuation_form",
    "contract_form",
    "casual_fair_work_statement",
    "bankAccount",
    "emergency_info",
  ];
  const sectionRouteMap: Record<string, string> = {
    tfn_form: "tfn-declaration",
    superannuation_form: "superannuation",
    contract_form: "contract-form",
    casual_fair_work_statement: "casual-and-fair-work-statement",
    bankAccount: "bank-account",
    emergency_info: "next-of-kin",
  };

  const { isStageReopenedFn, reopenedSections } = useReopenedStages();
  const isStage3Reopen = isStageReopenedFn(3);

  const handleNext = () => {
    dispatch(setTfnSuperformErrorMsgAction(''));

    if (isStage3Reopen && reopenedSections.length > 0) {
      const currentIndex = stage3Keys.indexOf("tfn_form");
      const stage3ReopenedSections = reopenedSections.filter(
        (key) => stage3Keys.includes(key)
      );
      const nextSection = stage3ReopenedSections.find(
        (key) => stage3Keys.indexOf(key) > currentIndex
      );
      if (nextSection) {
        const route = sectionRouteMap[nextSection];
        navigate(
          isAdmin
            ? `/admin/candidate-profile/${route}`
            : `/candidate/my-profile/${route}`
        );
        return;
      }
    }

    navigate(isAdmin ? "/admin/candidate-profile/superannuation" : "/candidate/my-profile/superannuation");
  };

  const clearRadioInputByName = (name: string): void => {
        if (!name || typeof name !== "string") {
            console.warn("Invalid name provided to clearRadioInputByName:", name);
            return;
        }
        const radioButtons = document.getElementsByName(name) as NodeListOf<HTMLInputElement>;
        if (radioButtons.length === 0) {
            console.warn(`No radio buttons found with the name: ${name}`);
            return;
        }
        
        radioButtons.forEach((radio) => {          
            if (radio.checked) {
            radio.checked = false; // Uncheck the selected radio button
            }
        });
        setDoTFNFieldValidation(true);
        setTfnFormFields((prevFields: FLKFormField[]) =>
          prevFields.map((field: FLKFormField) => {
            if (field?.name === name) {
              return { ...field, value: "" }; // Reset the value of the field
            }
            return field;
          })
        );        
    };

    useEffect(() => {
    if(FlkClientUrl && FlkClientUrl.length > 0) {
      document.querySelector(".flk-form")?.classList.add("max-w-1200px");
      document.querySelector(".flk-form")?.classList.remove("max-w-960px");
      setIsDisabled(false);
    } else {
      document.querySelector(".flk-form")?.classList.remove("max-w-1200px");
      document.querySelector(".flk-form")?.classList.add("max-w-960px");
      setIsDisabled(true);
    }
  }, [FlkClientUrl]);

  return (
   
    
    <div className="d-flex flex-column gap-20">
      {/* <div className="fs-30 fw-400 text-black">Review Contract</div>
      <div className="d-flex flex-column gap-10">
        <div className="fs-16 fw-400 text-black">
          Before you begin filling in the documents, please download and read
          the below contract that you will be required to sign in Step 3 of this
          stage.
        </div>
        <div className="d-flex">
          <button type="button" className="btn-theme bg-off-white text-black" onClick={openContractForm}>
            <Download />
            Contract.pdf
          </button>
        </div>
      </div>
      <div className="h-10px"></div> */}
      <div className="fs-30 fw-400 text-black">TFN Declaration</div>
      { !FlkPdfUrl ? <div className="bg-teal-light py-10px px-15px radius-8px d-flex gap-6">
        <Warning />
        <div className="fs-13 fw-400 text-black">
          The data collected from this form will be inserted into the official
          Tax File Number Declaration document using external software.
        </div>
      </div> : "" }
      { isImpersonating && 
        <div className={`bg-teal-light px-15px py-10px radius-8px d-flex gap-6`}>
          <Warning />
          <div className="fs-13 fw-400 text-black lh-sm">
            You are currently impersonating a candidate. You cannot sign the document while in impersonation mode.
          </div>
        </div>
      }
      {isProgress && (
        <div className="transparent-preloader">
          <Loader />
        </div>
      )}
      <Formik
        initialValues={tfnInitialValues}
        validateOnBlur={true}
        validateOnChange={true}
        onSubmit={(values, { setTouched }) => {
          const touchedFields = Object.keys(tfnInitialValues).reduce(
            (acc, key) => ({ ...acc, [key]: true }),
            {}
          );
          setTouched(touchedFields, true);
          handleFormSubmitValue(tfnInitialValues);
        }}
      >
        {({ errors, touched, isValidating, isValid, setFieldError, setFieldValue, setErrors }) => {
          dynamicIsDisabled = !isValid;

          return (
            <>
              <div className="tfn-form">
                <Form>
                  {tfnFormFields?.length > 0 && FlkClientUrl?.length === 0
                    ? tfnFormFields.map((tfnForm: FLKFormField) => (
                        <div
                          key={tfnForm.name}
                          className={tfnForm?.containerClass}
                        >
                          {tfnForm.type === "html" ? (
                            <div
                              className="com-html-text"
                              dangerouslySetInnerHTML={{
                                __html: tfnForm.value,
                              }}
                            ></div>
                          ) : null}

                          <div className={tfnForm?.className}>
                            <Label
                              label={tfnForm.label}
                              className={tfnForm?.lableClass}
                              subLabel={tfnForm?.subText}
                            />
                            <label className={tfnForm?.lableClass}>
                              {tfnForm?.additionalLabel}
                            </label>

                            {tfnForm.type === "split-input" ? (
                              <>
                                <SplitInput
                                  name={tfnForm.name}
                                  label={tfnForm.label}
                                  type={tfnForm.type}
                                  className={tfnForm.className}
                                  allData={tfnForm}
                                  maxlength={tfnForm.maxLength}
                                  onChange={(value: string) => {
                                    handleSetFormValues(tfnForm?.name, value);
                                    clearRadioInputByName(tfnForm?.pensionFieldName);
                                  }}
                                  doTFNFieldValidation={doTFNFieldValidation}
                                  error={errors}
                                  touched={touched}
                                  selectedValue={tfnForm?.value}

                                />
                              </>
                            ) : null}

                            {tfnForm.type === "radio-group" ? (
                              <>
                                <DynamicRadioInput
                                  option={tfnForm?.values}
                                  name={tfnForm?.name}
                                  allData={tfnForm}
                                  formik={tfnForm}
                                  selectedValue={tfnForm?.value}
                                  error={errors}
                                  onChange={() =>
                                    handleSetFormValues(
                                      tfnForm?.name,
                                      tfnForm?.value
                                    )
                                  }
                                  isValid={isValid}
                                  touched={touched}
                                  setFieldError={setFieldError}
                                  setFieldValue={setFieldValue}
                                  setDoTFNFieldValidation={setDoTFNFieldValidation}
                                  handleSetFormValues={handleSetFormValues}
                                  setErrors={setErrors}
                                  setClearTFNFieldValues={setClearTFNFieldValues}
                                  
                                />
                              </>
                            ) : null}
                          </div>
                        </div>
                      ))
                    : null}

                  {tfnFormFields?.length > 0 ? (
                    <div className="d-flex flex-column flex-md-row">
                      {/* <CustomButton
                        type="submit"
                        variant="primary"
                        disabled={dynamicIsDisabled}
                        className={`btn-theme ${
                          dynamicIsDisabled
                            ? "text-grey-medium bg-off-white"
                            : "bg-teal text-black"
                        }`}
                      >
                        Save and Continue{" "}
                        <RightArrow
                          color={dynamicIsDisabled ? "#757575" : "#000000"}
                        />
                      </CustomButton> */}

                      {
                        FlkClientUrl?.length === 0 && !isImpersonating && (                      
                        <CustomButton
                          type="submit"
                          variant="primary"
                          className="btn-theme bg-teal text-black"
                          onClick={() => {
                            // Find the first error field and scroll to it
                            const errorField = document.querySelector(
                              ".error-message"
                            );
                            if (errorField) {
                              (errorField as HTMLElement).scrollIntoView({ behavior: "smooth", block: "center" });
                              // Optionally, focus the field
                              if ((errorField as HTMLElement).focus) {
                                (errorField as HTMLElement).focus();
                              }
                            }
                          }}
                        >
                          Save and Continue{" "}
                          <RightArrow color={"#000000"} />
                        </CustomButton>
                        )}
                    </div>
                  ) : null}

                  {/* <>
                    {errors && errorSummary(errors, setErrors)}
                    {isValid ? "tr" : "fa"}
                    <ErrorMessage message={serverErrorMsg} />
                  </> */}
                </Form>
                { errorMsg !== "" ? <ErrorMessage className="mt-3" message={errorMsg} /> : ""}
              </div>
              <div>
                {FlkClientUrl?.length > 0 && (
                  <>
                    {
                      !isImpersonating && (
                        <iframe
                          src={FlkClientUrl}
                          title="flk-client"
                          className="flk-client"
                          style={{ width: "100%", height: "100vh" }}
                        ></iframe>
                      )
                    }
                    <div className="button-group justify-content-between mt-2">
                      <CustomButton
                        type="button"
                        variant="primary"
                        disabled={isDisabled}
                        className={`btn-theme ${
                          isDisabled
                            ? "text-grey-medium bg-off-white"
                            : "bg-teal text-black"
                        }`}
                        onClick={EditTfnForm}
                      >
                        <LeftArrow /> Edit Form
                      </CustomButton>

                      { (!isImpersonating && !isAdmin) && 
                        <CustomButton
                          type="button"
                          variant="primary"
                          disabled={isValidating}
                          className={`btn-theme ${
                            isDisabled
                              ? "text-grey-medium bg-off-white"
                              : "bg-teal text-black"
                          }`}
                          onClick={handleDocStatus}
                        >
                          Next{" "}
                          <RightArrow
                            color={isDisabled ? "#757575" : "#000000"}
                          />
                        </CustomButton>
                      }
                    </div>
                  </>
                )}

                

                {FlkPdfUrl?.length > 0 && (
                  <div className="d-flex flex-column gap-20">
                    <div className="fw-400 fs-16">Please click the link below to download a copy of this document for your records.</div>
                    <div className="d-flex">
                      <CustomButton
                        type="button"
                        className="btn-theme bg-teal text-white"
                        variant="primary"
                        onClick={() => handleDownloadPdf(FlkPdfUrl)}
                      >
                        <Download />
                        <div className="text-black">
                          Download TFN Declaration PDF
                        </div>
                      </CustomButton>
                    </div>
                    <div className="d-flex">
                      <CustomButton
                        type="submit"
                        variant="primary"
                        onClick={handleNext}
                        className="btn-theme bg-teal text-black mt-20"
                      >
                        Next
                        <RightArrow color={"#000000"} />
                      </CustomButton>
                    </div>
                  </div>
                )}
              </div>
            </>
          );
        }}
      </Formik>
    </div>
   
  );
};

export default TfnForm;
